/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   migrationControl.ts
 *     Description     :   TanStack Query hooks for the spec-003 Control-Plane REST API — useJobs and useJob.
 *     Module          :   005-web-shell
 *     Component Type  :   API
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-23      Peter Dobler        Initial creation
 *     1.1         2026-06-23      Peter Dobler        Reconcile to real MigrationControl DTO (no MigrationJob).
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   @tanstack/react-query @azure/msal-browser
 *     Internal        :   useAuth control-plane-types
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     fetchWithAuth useJobs useJob
 *
 * =========================================================================================
 */

import { useQuery } from "@tanstack/react-query";
import { getAccessToken } from "../auth/token.js";
import type { MigrationControl, ListEnvelope } from "@nextgenmigration/shared/control-plane-types";

const CONTROL_PLANE_URL = (import.meta.env["VITE_CONTROL_PLANE_URL"] as string | undefined) ?? "";

/** Acquires a bearer token (MSAL, or synthetic in bypass) and performs an authenticated fetch. */
export async function fetchWithAuth<T>(url: string): Promise<T> {
  const token = await getAccessToken();

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    // Surface the body's `error` when present (a 422 from Check Version validate/prepare carries the
    // real reason), not just the status code.
    let detail = "";
    try {
      const body = (await response.json()) as { error?: unknown; message?: unknown };
      detail = typeof body?.error === "string" ? body.error : typeof body?.message === "string" ? body.message : "";
    } catch {
      /* non-JSON error body — fall back to the status */
    }
    throw new Error(
      detail
        ? `Migration Control API request failed (${String(response.status)}): ${detail}`
        : `Migration Control API request failed: ${String(response.status)}`,
    );
  }

  return response.json() as Promise<T>;
}

/** TanStack Query hook: list all migration controls (polls every 30 s). */
export function useJobs(): ReturnType<typeof useQuery<ListEnvelope<MigrationControl>>> {
  return useQuery<ListEnvelope<MigrationControl>>({
    queryKey: ["jobs"],
    queryFn: () => fetchWithAuth<ListEnvelope<MigrationControl>>(`${CONTROL_PLANE_URL}/api/v1/jobs`),
    refetchInterval: 30_000,
  });
}

/** TanStack Query hook: single migration control by id. */
export function useJob(id: number): ReturnType<typeof useQuery<MigrationControl>> {
  return useQuery<MigrationControl>({
    queryKey: ["jobs", id],
    queryFn: () => fetchWithAuth<MigrationControl>(`${CONTROL_PLANE_URL}/api/v1/jobs/${String(id)}`),
    enabled: id > 0,
  });
}
